/**
 * App Versions API Service
 *
 * Handles all API calls related to app version management
 */

import api from '@/utils/axiosInterceptor';
import { AxiosError, AxiosResponse } from 'axios';

/**
 * API response types
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface AppVersionSummary {
  version: string;
  publishedAt: string;
}

export interface AppVersionDetails {
  _id: string;
  version: string;
  buildId: string;
  isForced: boolean;
  hasSeenVersion?: boolean;
  changelog: Array<{
    type: 'fix' | 'new feature';
    title: string;
    media?: string[];
  }>;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface VersionViewer {
  _id: string;
  empName: string;
  avatar?: string | null;
  seenAt: string;
}

export interface VersionViewsData {
  totalCount: number;
  uniqueUsers: number;
  viewers: VersionViewer[];
}

export interface VersionDetailsWithViews {
  version: AppVersionDetails;
  views: VersionViewsData;
}

/**
 * Error handling utility
 */
const handleApiError = (error: unknown): ApiResponse => {
  if (error instanceof AxiosError) {
    // Network error - no response from server
    if (!error.response) {
      console.error('Network error:', error.message);
      return {
        success: false,
        message: 'Connection error',
        error: error.message,
      };
    }

    // Server error with response
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Unknown error occurred';
    return {
      success: false,
      message: errorMessage,
      error: errorMessage,
    };
  }

  // Unknown error type
  console.error('Unexpected error:', error);
  return {
    success: false,
    message: 'An unexpected error occurred',
    error: String(error),
  };
};

/**
 * Get all app versions summary (version and publish date only)
 */
export const getAllVersions = async (): Promise<
  ApiResponse<AppVersionSummary[]>
> => {
  try {
    const response: AxiosResponse<ApiResponse<AppVersionSummary[]>> =
      await api.get('/app/version/all');

    return response.data;
  } catch (error) {
    console.error('❌ [getAllVersions] Error fetching versions:', error);
    return handleApiError(error);
  }
};

/**
 * Get latest app version with full details
 */
export const getLatestVersionDetails = async (): Promise<
  ApiResponse<AppVersionDetails>
> => {
  try {

    const response: AxiosResponse<ApiResponse<AppVersionDetails>> =
      await api.get('/app/version/latest');

    return response.data;
  } catch (error) {
    console.error(
      '❌ [getLatestVersionDetails] Error fetching latest version:',
      error,
    );
    return handleApiError(error);
  }
};

/**
 * Get specific version details by version number
 */
export const getVersionDetails = async (
  version: string,
): Promise<ApiResponse<AppVersionDetails>> => {
  try {

    const response: AxiosResponse<ApiResponse<AppVersionDetails>> =
      await api.get(`/app/version/${version}`);

    return response.data;
  } catch (error) {
    console.error(
      '❌ [getVersionDetails] Error fetching version details:',
      error,
    );
    return handleApiError(error);
  }
};

/**
 * Get specific version details for authenticated users
 */
export const getVersionDetailsAuth = async (
  version: string,
): Promise<ApiResponse<AppVersionDetails>> => {
  try {

    const response: AxiosResponse<ApiResponse<AppVersionDetails>> =
      await api.get(`/app/version/details/${version}`);

    return response.data;
  } catch (error) {
    console.error(
      '❌ [getVersionDetailsAuth] Error fetching version details:',
      error,
    );
    return handleApiError(error);
  }
};

/**
 * Publishing API Types
 */
export interface ChangelogItemDto {
  type: 'fix' | 'new feature';
  title: string;
  media?: string[];
}

export interface PublishVersionDto {
  version: string;
  buildId: string;
  isForced: boolean;
  changelog: ChangelogItemDto[];
}

/**
 * Publish a new app version
 */
export const publishVersion = async (
  data: PublishVersionDto,
): Promise<ApiResponse<AppVersionDetails>> => {
  try {

    const response: AxiosResponse<ApiResponse<AppVersionDetails>> =
      await api.post('/app/version/publish', data);

    return response.data;
  } catch (error) {
    console.error('❌ [publishVersion] Error publishing version:', error);
    return handleApiError(error);
  }
};

/**
 * Get specific version details with view statistics (Admin only)
 */
export const getVersionDetailsWithViews = async (
  version: string,
): Promise<ApiResponse<VersionDetailsWithViews>> => {
  try {

    const response: AxiosResponse<ApiResponse<VersionDetailsWithViews>> =
      await api.get(`/app/version/admin/details/${version}`);

    return response.data;
  } catch (error) {
    console.error(
      '❌ [getVersionDetailsWithViews] Error fetching version details with views:',
      error,
    );
    return handleApiError(error);
  }
};
